// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import $ from 'jquery';
import WatchlistComboBoxTemplate from '@front/templates/WatchlistComboBoxTemplate.html';
import { Resources } from '@shared/localizations/Resources';
import { NewContextMenuItemType } from '@shared/utils/ContextMenuUtils';
import { CustomEvent } from '@shared/utils/CustomEvents';
import { MessageBoxType } from '@shared/utils/Interfaces/Screens/MessageBox.types';
import { WatchlistManager, WatchlistManagerEvents } from '@shared/utils/Managers/WatchlistManager/WatchlistManager';
import { _WatchlistSheets } from '@shared/utils/Managers/WatchlistManager/WatchlistSheets';
import { Control } from './Control';
import { TerceraMessageBox } from '../screen/TerceraMessageBox';
import { TerceraRenameScreen } from '../screen/TerceraRenameScreen';
import { ControlsTypes } from '../UtilsClasses/FactoryConstants';
export class WatchlistComboBox extends Control {
    get wlManager() {
        return WatchlistManager;
    }
    getType() {
        return ControlsTypes.WatchlistComboBox;
    }
    get selectedItemName() {
        return this.get('selectedItem');
    }
    set selectedItemName(name) {
        void this.set('selectedItem', name);
    }
    constructor() {
        super();
        this.menuItems = [];
        this.OnSelectList = new CustomEvent();
        this.onSymbolsChanged = () => {
            const item = this.wlManager.getItemByName(this.selectedItemName);
            this.OnSelectList.Raise(item.getInstrumentDescriptions(), item.name);
        };
    }
    oninit() {
        this.observe('items', this.onItemsChanged);
    }
    setSelectedItemSymbols(instrDescriptions) {
        this.wlManager.setNewSymbolsForItem(this.selectedItemName, instrDescriptions);
    }
    getItems() {
        return Array.from(this.wlManager.getItems());
    }
    oncomplete() {
        super.oncomplete();
        this.wlManager.subscribeEvent(WatchlistManagerEvents.sheetUpdated, this.onSymbolsChanged);
        _WatchlistSheets.itemsChanged.Subscribe(this.onItemsChanged, this);
        if (isNullOrUndefined(this.selectedItemName) || this.wlManager.getItemByName(this.selectedItemName) === null) {
            this.setDefaultSelectedList();
        }
    }
    dispose() {
        this.wlManager.unsubscribeEvent(WatchlistManagerEvents.sheetUpdated, this.onSymbolsChanged);
        _WatchlistSheets.itemsChanged.UnSubscribe(this.onItemsChanged, this);
        super.dispose();
    }
    onItemsChanged() {
        this.menuItems = this.createMenuItems();
        void this.set('items', this.menuItems);
    }
    showMenu() {
        const offset = $(this.find('div')).offset();
        const posY = offset.top - $(window).scrollTop() + 32 + 1; // +1 for space between combobox and menu, +32 - height of combobox
        const posX = offset.left - $(window).scrollLeft();
        void this.set({ isMenuShown: true });
        showContextMenu({
            x: posX,
            y: posY,
            items: this.menuItems,
            functions: {
                closeCallback: () => {
                    this.onContextMenuClosed();
                }
            }
        });
    }
    hideMenu() {
        void this.set({ isMenuShown: false });
        hideContextMenu();
    }
    private_OnMenuItemSelected(menuItem) {
        this.setNewSelectedList(menuItem.text);
        void this.set({ isMenuShown: false });
    }
    private_BtnClick(sender) {
        const isEnabled = this.get('enabled');
        if (!isEnabled) {
            return;
        }
        const isMenuShown = this.get('isMenuShown');
        if (isMenuShown) {
            this.hideMenu();
            this.lostFocus();
            return;
        }
        this.showMenu();
    }
    restoreCorrectSelectItem(selectedText) {
        const items = this.getItems();
        for (let i = 0; i < items.length; i++) {
            if (items[i].name === selectedText) {
                this.setNewSelectedList(selectedText);
                this.onItemsChanged();
                break;
            }
        }
    }
    createNewClicked() {
        const header = this.get('newItemHeader');
        const preferredName = this.wlManager.getPreferredItemName();
        TerceraRenameScreen.show(preferredName, (name) => this.createItem(name), Resources.getResource(header));
    }
    createItem(listName) {
        const header = this.get('newItemHeader');
        const error = this.wlManager.addItem(listName);
        if (error !== null) {
            this.showError(listName, error, Resources.getResource(header), (retryName) => this.createItem(retryName));
            return;
        }
        this.setNewSelectedList(listName);
        this.onItemsChanged();
    }
    renameItem(menuItem, listName) {
        if (isNullOrUndefined(menuItem) || !isValidString(listName))
            return;
        const header = this.get('renameHeader');
        const error = this.wlManager.renameItem(menuItem.text, listName);
        if (error !== null) {
            this.showError(listName, error, Resources.getResource(header), (newName) => this.renameItem(menuItem, newName));
            return;
        }
        if (menuItem.selected) {
            this.selectedItemName = listName;
        }
        this.onItemsChanged();
    }
    deleteItemConfirm(menuItem) {
        if (isNullOrUndefined(menuItem))
            return;
        const error = this.wlManager.validateItemRemove();
        if (error !== null) {
            const header = Resources.getResource('screen.error.title');
            this.showError(menuItem.text, error, Resources.getResource(header), () => this.deleteItemConfirm(menuItem));
            return;
        }
        TerceraMessageBox.Show(Resources.getResource('screen.remove.title'), `${Resources.getResource('screen.remove.confirmText.firstPart')}'${menuItem.text}'${Resources.getResource(this.get('removeTxtSecondPart'))}`, MessageBoxType.Question, () => this.deleteItem(menuItem));
    }
    deleteItem(menuItem) {
        this.wlManager.removeItem(menuItem.text);
        this.onItemsChanged();
        if (this.selectedItemName === menuItem.text) {
            this.setDefaultSelectedList();
        }
    }
    updateSelection(selectedName) {
        if (isNullOrUndefined(this.menuItems))
            return;
        for (const item of this.menuItems) {
            item.selected = item.text === selectedName;
        }
    }
    showError(newName, error, renameScreenHeader, renameScreenCallBack) {
        TerceraMessageBox.Show(Resources.getResource('screen.error.title'), error, MessageBoxType.Error, null, null, false, true, null, { zIndex: 2100 });
        // TerceraRenameScreen.show(newName, renameScreenCallBack, renameScreenHeader);
    }
    createMenuItems() {
        const items = this.getItems();
        const menuItems = [];
        const selectedItem = this.wlManager.getItemByName(this.selectedItemName);
        const additionalActions = [
            {
                svgImage: 'editIcon',
                tooltip: 'panel.menu.renameList',
                event: (item) => {
                    TerceraRenameScreen.show(item.text, (newName) => this.renameItem(item, newName), Resources.getResource(this.get('renameHeader')));
                    hideContextMenu();
                }
            },
            {
                svgImage: 'closeCrossBtn',
                tooltip: 'panel.menu.removeList',
                event: (item) => {
                    this.deleteItemConfirm(item);
                    hideContextMenu();
                }
            }
        ];
        for (let i = 0; i < items.length; i++) {
            const item = items[i];
            const menuItem = {
                type: NewContextMenuItemType.Default,
                text: item.name,
                selected: !isNullOrUndefined(selectedItem) ? selectedItem.name === item.name : false,
                event: (clickedMenuItem) => {
                    this.private_OnMenuItemSelected(clickedMenuItem);
                },
                additionalActions
            };
            menuItems.push(menuItem);
        }
        if (menuItems.length > 0) {
            menuItems.push({ type: NewContextMenuItemType.Separator });
        }
        menuItems.push({
            type: NewContextMenuItemType.Default,
            text: 'editableComboBox.CreateNew',
            tooltip: 'editableComboBox.CreateNew.tooltip',
            event: (item) => {
                this.createNewClicked();
            }
        });
        return menuItems;
    }
    onContextMenuClosed() {
        void this.set({ isMenuShown: false });
        this.lostFocus();
    }
    onMouseDown(context) {
        super.onMouseDown(context);
        if (this.get('isMenuShown')) {
            context.original.stopPropagation();
        }
    }
    setNewSelectedList(newListName) {
        this.selectedItemName = newListName;
        this.updateSelection(newListName);
        const item = this.wlManager.getItemByName(newListName);
        this.OnSelectList.Raise(item.getInstrumentDescriptions(), item.name);
    }
    setDefaultSelectedList() {
        const firstItem = this.wlManager.getFirstItem();
        this.setNewSelectedList(firstItem.name);
    }
}
WatchlistComboBox.DROPDOWN_MIN_WIDTH = 200;
WatchlistComboBox.DROPDOWN_MAX_WIDTH = 350;
WatchlistComboBox.DROPDOWN_LEFT_OFFSET = 16;
WatchlistComboBox.DEFAUL_VALUE_SEPARATOR = 1433;
WatchlistComboBox.VALUE_DEFAUL = 0;
Control.extendWith(WatchlistComboBox, {
    template: WatchlistComboBoxTemplate,
    data: function () {
        return {
            storageName: null,
            saveImage: false,
            renameHeader: 'screen.renameScreen.rename.header',
            newItemHeader: 'screen.renameScreen.newList.header',
            removeTxtSecondPart: 'screen.remove.confirmText.secondPart',
            enabled: true,
            showLastValue: true,
            items: [],
            selectedItem: null,
            tooltip: '',
            elementStyle: 'js-combobox',
            additionalClass: '',
            useSVGView: false,
            usePNGView: false,
            showArrow: true,
            infoBtnTooltip: '',
            updateComboBoxClass: '',
            isPosAbsolute: false,
            isMenuShown: false
        };
    }
});
// Events
export var WatchlistComboBoxEvents;
(function (WatchlistComboBoxEvents) {
    WatchlistComboBoxEvents["ValueChange"] = "ValueChange";
    WatchlistComboBoxEvents["ComboItemClicked"] = "ComboItemClicked";
})(WatchlistComboBoxEvents || (WatchlistComboBoxEvents = {}));
